import { isFeeProduct, isSizeSellable } from './pricing';

/**
 * Ποια μεγέθη πουλάει το ταμείο — ο ορισμός (§3.1, αμετάβλητος) ζει πλέον στο
 * utils/pricing.ts, δίπλα στο defaultSize που τον χρειάζεται· εδώ μόνο ξανα-εξάγεται για
 * τους υπάρχοντες καλούντες. Όχι αντίστροφα: το sellableSizes εισάγει ήδη από το pricing
 * και θα γινόταν κυκλικό import.
 *
 * Εδώ ζει το φίλτρο για τις οθόνες που φορτώνουν ΧΩΡΙΣ available_only (delivery, dine-in,
 * σερβιτόροι) — εκείνες παίρνουν όλα τα μεγέθη με τις σημαίες.
 */
export { isSizeSellable };

type WithSizes = {
  fee_category?: number | null;
  is_open_price?: boolean;
  sizes?: Array<{ is_available?: boolean; is_hidden?: boolean }>;
};

/**
 * Κατάλογος όπως τον βλέπει το ταμείο: κρύβει τα μη πωλήσιμα μεγέθη.
 *
 * Ορατό = τέλος Ή ανοιχτή τιμή Ή ≥1 πωλήσιμο μέγεθος — ίδιο φίλτρο με τη λίστα POS του server
 * (GET /stores/:id/products με available_only). Η τιμή ζει στο μέγεθος, άρα μη-τέλος χωρίς
 * πωλήσιμο μέγεθος δεν έχει τιμή και βγαίνει εντελώς (αλλιώς θα πουλιόταν €0). Το τέλος ΔΕΝ
 * έχει μέγεθος (απόφαση 1_fees_source): χωρίς την εξαίρεση θα έφευγε και η αυτόματη γραμμή
 * τέλους θα σιωπούσε. Η ανοιχτή τιμή μένει: το ποσό το πληκτρολογεί ο ταμίας.
 */
export function withSellableSizes<T extends WithSizes>(products: T[]): T[] {
  const out: T[] = [];
  for (const p of products) {
    const all = p.sizes || [];
    const sizes = all.filter(isSizeSellable);
    if (sizes.length === 0 && !isFeeProduct(p) && !p.is_open_price) continue;
    out.push(sizes.length === all.length ? p : { ...p, sizes });
  }
  return out;
}

/**
 * Απόφαση 15: είναι το `sizeId` το ΜΟΝΑΔΙΚΟ πωλήσιμο μέγεθος του προϊόντος;
 *
 * ΓΙΑΤΙ: στο μοντέλο «τιμή μόνο στο μέγεθος» κάθε μονομεγέθες αποκτά ένα «Κανονικό»·
 * γραμμένο σε καλάθι/κουζίνα θα διαβαζόταν ως επιλογή του πελάτη. Κρύβουμε ΜΟΝΟ αυτό
 * το «αυτονόητο» μέγεθος. Ίδιος κανόνας με τον backend (utils/singleSizeName.js,
 * onlySellableSize §3.8): μετρούν ΜΟΝΟ τα πωλήσιμα — ένα κρυφό ή κεντρικά αφαιρεμένο
 * δεύτερο μέγεθος δεν το κάνει «πολυμεγέθες», αλλιώς η οθόνη και το χαρτί θα διαφωνούσαν.
 * Εδώ ζει ο κανόνας ΜΙΑ φορά για καλάθι (BasketItemRow), «Size:» (orderHelpers) και dine-in.
 *
 * Χωρίς την εξαίρεση F1-W3-2 της Φ1 («Κανονικό» χωρίς μέγεθος δίπλα στο μοναδικό μέγεθος):
 * μετά το κλείδωμα της Φ4 κανένα προϊόν δεν έχει τιμή εκτός μεγέθους, άρα η εξαίρεση δεν έχει
 * περιπτώσεις — όπως και στο backend.
 *
 * Προϊόν χωρίς φορτωμένα μεγέθη ή μέγεθος χωρίς id → false (εμφανίζεται, όπως σήμερα):
 * σε αμφιβολία δεν κρύβουμε.
 */
export function isOnlySellableSize(
  product: {
    sizes?: Array<{ id: number | string; is_available?: boolean | null; is_hidden?: boolean | null }> | null;
  } | null | undefined,
  sizeId: number | string | null | undefined
): boolean {
  if (!product || sizeId === null || sizeId === undefined || sizeId === '') return false;
  const sellable = (product.sizes || []).filter(isSizeSellable);
  return sellable.length === 1 && Number(sellable[0].id) === Number(sizeId);
}

/**
 * Απόφαση F1-W4-4: προϊόν-τέλος (isFeeProduct του pricing.ts, §3.4) δεν δείχνει ΠΟΤΕ όνομα
 * μεγέθους. Τα τέλη δεν έχουν μέγεθος (απόφαση 1)· ό,τι μέγεθος κουβαλά μια γραμμή τέλους
 * είναι κατάλοιπο (τα μεγέθη τους αποσύρονται στη μετάπτωση, F1-W3-15) και στο
 * καλάθι/κουζίνα θα διαβαζόταν ως «σακούλα Μεγάλη». Δίδυμο: backend utils/singleSizeName.js
 * loadFeeProductIds/isFeeItem.
 */
const isFee = (product: { fee_category?: number | null } | null | undefined): boolean =>
  !!product && isFeeProduct(product);

/**
 * Κρύβεται το όνομα του μεγέθους `sizeId` για γραμμή που ΚΟΥΒΑΛΑ το αντικείμενο του προϊόντος
 * (καλάθι ταμείου — BasketItemRow, «Size:» του delivery — orderHelpers);
 *  - τέλος → ΠΑΝΤΑ (F1-W4-4)·
 *  - αλλιώς όταν είναι το μοναδικό πωλήσιμο μέγεθος (isOnlySellableSize).
 * ΧΩΡΙΣ διακόπτη (απόφαση F1-S1, 30/9 μετά τις δοκιμές staging): ο διακόπτης «Εμφάνιση
 * μεγεθών» (store.print_hide_single_size_name) κρίθηκε άχρηστος — κανένα κατάστημα δεν θέλει
 * «Κανονικό» σε κάθε γραμμή. Ένα σημείο για τον κανόνα, ώστε οθόνη και χαρτί (visibleSizeName
 * του backend) να συμφωνούν.
 */
export function hidesSizeName(
  product: (Parameters<typeof isOnlySellableSize>[0] & { fee_category?: number | null }) | null | undefined,
  sizeId: number | string | null | undefined
): boolean {
  if (isFee(product)) return true;
  return isOnlySellableSize(product, sizeId);
}

/**
 * Κανονικοποίηση ονόματος μεγέθους για σύγκριση — δίδυμο του `norm` του backend
 * (utils/singleSizeName.js): trim, πεζά, χωρίς τόνους, «ς» → «σ». Ίδια συνάρτηση και στις δύο
 * πλευρές, αλλιώς το «ΚΑΝΟΝΙΚΌ» θα κρυβόταν στο χαρτί και θα φαινόταν στην οθόνη.
 */
function normSizeName(s: unknown): string {
  if (typeof s !== 'string') return '';
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase().replace(/ς/g, 'σ');
}

type SizeRef =
  | number
  | string
  | { id?: number | string | null; name?: string | null }
  | null
  | undefined;

/**
 * Απόφαση 15 (FE-10b): δείχνεται το όνομα μεγέθους μιας γραμμής που ΔΕΝ κουβαλά το αντικείμενο
 * του προϊόντος (γραμμές τραπεζιού από τον server, καλάθι dine-in, app σερβιτόρου)·
 * το προϊόν βρίσκεται στον κατάλογο `products` που έχει ήδη φορτώσει η οθόνη.
 *
 * `sizeIdOrName`: αριθμός = id μεγέθους, κείμενο = όνομα, ή `{ id, name }` όταν η γραμμή έχει
 * και τα δύο. Ταιριάζει πρώτα με id και μετά με κανονικοποιημένο όνομα — ΟΠΩΣ το shouldHide
 * του backend: οι γραμμές dine-in έχουν size_id, αλλά παλιότερες/εξωτερικές μόνο όνομα, και η
 * οθόνη πρέπει να συμφωνεί με το χαρτί κουζίνας/απόδειξης.
 *
 * Ο κανόνας «μοναδικό πωλήσιμο» ΔΕΝ ξαναγράφεται εδώ: κάθε σύγκριση περνά από το
 * isOnlySellableSize, ώστε μια μελλοντική εξαίρεση να ισχύσει παντού με μία αλλαγή.
 *
 * Προϊόν-τέλος (F1-W4-4) → ΠΟΤΕ. Χωρίς διακόπτη (F1-S1): το μοναδικό κρύβεται πάντα.
 *
 * Σε αμφιβολία δείχνει (όπως πριν τη Φάση 1): άγνωστο προϊόν (π.χ. κατάλογος που δεν φόρτωσε
 * ακόμη, προϊόν που αποσύρθηκε), χωρίς id και όνομα.
 */
export function showSizeName(
  products: ReadonlyArray<{ id: number | string; fee_category?: number | null; sizes?: Array<{ id: number | string; name?: string | null; is_available?: boolean | null; is_hidden?: boolean | null }> | null }> | null | undefined,
  productId: number | string | null | undefined,
  sizeIdOrName: SizeRef
): boolean {
  if (productId === null || productId === undefined || sizeIdOrName == null) return true;
  const product = (products || []).find(p => Number(p.id) === Number(productId));
  if (!product) return true;
  if (isFee(product)) return false;

  const ref = typeof sizeIdOrName === 'number'
    ? { id: sizeIdOrName, name: null }
    : typeof sizeIdOrName === 'string'
      ? { id: null, name: sizeIdOrName }
      : sizeIdOrName;

  if (ref.id !== null && ref.id !== undefined && isOnlySellableSize(product, ref.id)) return false;

  const n = normSizeName(ref.name);
  if (n === '') return true;
  const byName = (product.sizes || []).some(s => normSizeName(s.name) === n && isOnlySellableSize(product, s.id));
  return !byName;
}

/**
 * Απόφαση P5-E19 (ιδιοκτήτης 2/10/2026): όνομα γραμμής με το μέγεθος («Κουλούρι Μεγάλο») για
 * γραμμές που κρατούν ΜΟΝΟ όνομα (Επεξεργασία ειδών πλατφόρμας — απόδειξη, κουζίνα και λίστα
 * διαβάζουν το product_name). Το αν φαίνεται το μέγεθος το κρίνει το hidesSizeName (μοναδικό
 * πωλήσιμο ή τέλος → όπως ήρθε). Δίδυμο: backend utils/singleSizeName.js lineNameWithSize,
 * που επίσης δεν ξαναγράφει μέγεθος με το οποίο το όνομα ήδη τελειώνει.
 */
export function lineNameWithSize(
  baseName: string,
  product: Parameters<typeof hidesSizeName>[0],
  size: { id: number | string; name?: string | null } | null | undefined
): string {
  const base = (baseName || '').trim();
  const sizeName = (size?.name || '').trim();
  if (!base || !sizeName || !size || hidesSizeName(product, size.id)) return base;
  const b = normSizeName(base);
  const n = normSizeName(sizeName);
  if (b === n || b.endsWith(` ${n}`)) return base;
  return `${base} ${sizeName}`;
}

/**
 * Το όνομα μεγέθους που δείχνει το KDS για ένα είδος. Το `hide_size_name` το υπολογίζει ο
 * backend (GET /dine-in/:storeId/kitchen/orders, BE-16/F1-S1: μοναδικό πωλήσιμο, ή
 * προϊόν-τέλος F1-W4-4) — η οθόνη δεν ξαναπαίρνει απόφαση, για να μη διαφωνεί με το χαρτί της κουζίνας.
 * Κρύβει ΜΟΝΟ όταν είναι ρητά true: παλιό backend χωρίς το πεδίο = εμφάνιση, όπως σήμερα.
 */
export function kitchenSizeName(item: { size_name?: string | null; hide_size_name?: boolean | null }): string | null {
  if (!item.size_name || item.hide_size_name === true) return null;
  return item.size_name;
}
